<template>
  <b-modal
    id="reset-otp-confirm"
    ref="modalResetOtp"
    :title="isReactivate ? 'Xác nhận kích hoạt lại Soft OTP' : 'Xác nhận reset mã xác thực'"
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <div class="reset-otp otp-scope">
      <p v-if="isReactivate">
        Thao tác này sẽ <b>hủy toàn bộ khóa và thiết bị cũ</b>, sinh
        <b>mã kích hoạt mới</b> và <b>gửi email</b> hướng dẫn đăng ký lại cho người dùng.
      </p>
      <p v-else>
        Thao tác này sẽ <b>huỷ toàn bộ thiết bị xác thực 2 bước</b> và
        <b>đăng xuất</b> người dùng được reset (buộc thiết lập lại 2FA).
      </p>
      <!-- Step-up OTP admin: chỉ hiện khi CÒN yêu cầu nhập OTP. Cờ SysConfig Otp.AdminResetStepUpEnabled=false
           (chỉ áp cho mode reset/disable, KHÔNG áp reactivate) -> ẩn ô OTP, admin reset không cần nhập OTP. -->
      <template v-if="stepUpRequired">
        <p class="mt-1">
          Nhập mã OTP hiện tại của <b>chính bạn</b> để xác nhận (bạn phải đang bật
          xác thực 2 bước).
        </p>
        <div class="mb-1 font-weight-bold">Mã xác thực của bạn</div>
        <b-form-input
          v-otp-autofocus
          ref="inputOtp"
          v-model="otp"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          pattern="[0-9]*"
          class="outline-primary input"
          placeholder="Mã OTP 6 số"
          @keyup.enter="confirm"
        />
      </template>

      <!-- GAP 10.3 (UC10.3): lý do reset (không bắt buộc). Ghi vào OtpAuditLog.Reason.
           maxlength 200 khớp cột nvarchar(200); bỏ trống -> BE fallback "admin-reset-2fa". -->
      <div class="mb-1 mt-2 font-weight-bold">Lý do (không bắt buộc)</div>
      <b-form-textarea
        v-model="reason"
        class="outline-primary"
        placeholder="Nhập lý do reset (nếu có)"
        rows="2"
        max-rows="4"
        maxlength="200"
      />
    </div>

    <template v-slot:modal-footer>
      <div class="w-100 text-right">
        <base-button
          :button="isReactivate ? 'Xác nhận kích hoạt lại' : 'Xác nhận reset'"
          :disabled="stepUpRequired && !otp"
          @click="confirm"
        />
        <b-button variant="outline-primary" class="ml-1" @click="hideModal"
          >Đóng</b-button
        >
      </div>
    </template>
    <BaseLoading :is-loading="isLoading" />
  </b-modal>
</template>

<script>
import BaseLoading from "../../common/BaseLoading";
// KHONG gui deviceId khi step-up admin OTP: getOrCreateDeviceId() = UUID localStorage browser, KHAC
// Device.DeviceId (app QLVB sinh khi enroll QR). Admin sinh OTP tu app mobile -> gui deviceId=null de BE
// fan-out VerifyWithActiveDevicesFallbackAsync (multi-credential, xem TwoFactorAuthenticatorService line 118
// P2 QR handoff Bug 8). Neu gui deviceId browser -> RunOtpSecurityPipelineAsync khong tim ra Device
// khop -> "device-invalid".

// Phase 7A: xác nhận reset 2FA của user khác. STEP-UP: admin nhập OTP CHÍNH MÌNH ->
// gửi { userId (target), twoFactorCode (OTP admin), deviceId } sang reset-setting-2fa.
// BE verify OTP admin trước khi reset (revoke device + huỷ session target + audit).
export default {
  name: "ModalResetOtpConfirm",
  components: { BaseLoading },
  data() {
    return {
      targetUserId: null,
      otp: null,
      reason: null,
      isLoading: false,
      // mode: 'disable' (reset/vô hiệu - mặc định, hành vi cũ) | 'reactivate' (UC10.4 kích hoạt lại).
      mode: "disable",
      // Step-up OTP admin còn bắt buộc không. Default TRUE = fail-safe (giữ hành vi cũ: bắt nhập OTP)
      // khi CHƯA load được cờ hoặc getOtpConfig lỗi. Chỉ hạ FALSE khi cờ SysConfig
      // Otp.AdminResetStepUpEnabled=false VÀ mode='disable' (reactivate LUÔN cần step-up).
      stepUpRequired: true,
    };
  },
  computed: {
    isReactivate() {
      return this.mode === "reactivate";
    },
  },
  methods: {
    // mode mặc định 'disable' -> caller cũ (chỉ truyền targetUserId) giữ nguyên hành vi (zero-regression).
    showModal(targetUserId, mode = "disable") {
      this.targetUserId = targetUserId;
      this.mode = mode;
      this.otp = null;
      this.reason = null;
      // Fail-safe: mặc định bắt step-up. 2026-09-16: cờ AdminResetStepUpEnabled áp CHO CẢ 3 luồng
      // (disable/reset + reactivate + enable) -> load cờ cho MỌI mode (không chỉ disable). Cờ tắt -> ẩn ô OTP.
      this.stepUpRequired = true;
      this.loadStepUpFlag();
      this.$refs.modalResetOtp.show();
    },
    // Đọc cờ SysConfig Otp.AdminResetStepUpEnabled (BE echo qua getOtpConfig). false -> bỏ bắt nhập OTP.
    // Lỗi/không đọc được -> GIỮ stepUpRequired=true (fail-safe: thà bắt nhập OTP còn hơn bỏ nhầm).
    loadStepUpFlag() {
      this.$store
        .dispatch("common/getOtpConfig")
        .then((res) => {
          if (res && res.succeeded && res.data && res.data.adminResetStepUpEnabled === false) {
            this.stepUpRequired = false;
          }
        })
        .catch(() => {
          // giữ stepUpRequired=true (fail-safe)
        });
    },
    hideModal() {
      this.otp = null;
      this.reason = null;
      this.$refs.modalResetOtp.hide();
    },
    confirm() {
      this.isLoading = true;
      if (this.isReactivate) {
        // UC10.4: reactivate qua store common (resolve response.data camelCase). Step-up OTP admin.
        this.$store
          .dispatch("common/reactivate2Fa", {
            userId: this.targetUserId,
            twoFactorCode: this.otp,
            // deviceId: null -> BE fan-out verify voi tat ca Device Active cua admin (WebAuthn multi-cred).
            reason: this.reason,
          })
          .then((res) => {
            this.isLoading = false;
            if (res && res.succeeded) {
              this.$toasted.clear();
              this.$toasted.global.showSuccessMessage({
                message: "Kích hoạt lại thành công - đã gửi mã kích hoạt qua email cho người dùng",
              });
              this.$emit("reset-done");
              this.hideModal();
            }
          })
          .catch((error) => {
            this.isLoading = false;
            this.$toasted.global.showErrorMessage({
              message:
                (error && error.data && error.data.message) ||
                "Kích hoạt lại thất bại, vui lòng kiểm tra mã xác thực.",
            });
          });
        return;
      }
      this.$store
        .dispatch("user/resetQRUser", {
          userId: this.targetUserId,
          // Cờ tắt step-up -> gửi null (BE bỏ verify). Cờ bật -> gửi OTP admin nhập.
          twoFactorCode: this.stepUpRequired ? this.otp : null,
          // deviceId: null -> BE fan-out verify voi tat ca Device Active cua admin (WebAuthn multi-cred).
          // GAP 10.3: lý do (optional). Bỏ trống -> BE fallback "admin-reset-2fa".
          reason: this.reason,
        })
        .then((res) => {
          this.isLoading = false;
          // resetQRUser resolve axios response -> body ở res.data (ServiceResponse camelCase).
          if (res.data.succeeded) {
            this.$toasted.clear();
            this.$toasted.global.showSuccessMessage({
              message: "Reset mã xác thực thành công",
            });
            this.$emit("reset-done");
            this.hideModal();
          }
        })
        .catch(() => {
          // action resetQRUser đã show error toast (message step-up sai / admin chưa 2FA).
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
// Reskin OTP (Phase 4): overlay token. Giữ hợp đồng showModal(targetUserId,mode) + emit reset-done.
// Payload OMIT deviceId (RT-4) - KHÔNG đụng script.
.reset-otp {
  font-family: var(--otp-sans);
  font-size: 14px;
  color: var(--otp-ink);
  .input {
    border: 1px solid var(--otp-border-strong);
    border-radius: var(--otp-radius-sm);
    height: 36px;
    &:focus { border-color: var(--otp-blue); box-shadow: var(--otp-ring); outline: none; }
  }
}
</style>
